import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { CloseCross, ChevronUp, ChevronDown } from '../../icons';
import { useIsInBottomSheet } from '../../providers';
import { BottomSheetContentLayout } from '../BottomSheet/BottomSheetContentLayout/BottomSheetContentLayout';
import { Button } from '../Button';
import { ChipButton } from '../ChipButton';
import { CloseButton } from '../CloseButton';
import { useSheetStack } from '../OverlayContext/hooks/useSheetStack';
import { Popover } from '../Popover';
import { TextButton } from '../TextButton';
import { Typography } from '../Typography';
import styles from './FilterPicker.module.scss';
export const FilterPicker = (props)=>{
    const { children, title, label, removable, disableApply, headerContent, startOpen = false, popoverWidth, hasError, onRemove, onClose, onApply, highlightTrigger, type = 'chip', testId, applyLabel = 'Apply', closeDelayMs = 100 } = props;
    const isInBottomSheet = useIsInBottomSheet();
    const { popBottomSheetPage } = useSheetStack();
    const anchorElRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isHighlighted, setIsHilighted] = useState(highlightTrigger);
    function closePopover(options) {
        setAnchorEl(null);
        setTimeout(()=>{
            onClose(options);
        }, closeDelayMs);
    }
    function handleApply() {
        if (isInBottomSheet) {
            popBottomSheetPage();
        }
        onApply();
        closePopover({
            revert: false
        });
    }
    useEffect(()=>{
        if (anchorEl) {
            setIsHilighted(true);
        } else {
            setIsHilighted(highlightTrigger);
        }
    }, [
        anchorEl,
        highlightTrigger
    ]);
    useEffect(()=>{
        if (startOpen && anchorElRef.current) {
            setAnchorEl(anchorElRef.current);
        }
    }, []);
    return isInBottomSheet ? /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        header: {
            title,
            subtitle: headerContent,
            gutter: 'm'
        },
        contentGutter: "m",
        footer: {
            primaryAction: {
                text: applyLabel,
                onClick: handleApply,
                disabled: disableApply,
                testId: "".concat(testId, "-apply"),
                // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- FilterPicker is a building block; consumers (SingleSelect/MultiSelect) own the filter selection BI, so a generic apply event would be redundant noise
                noBi: true
            }
        },
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.filters,
            children: children
        })
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.chip,
                "data-testid": testId,
                children: [
                    type === 'chip' && /*#__PURE__*/ _jsx(ChipButton, {
                        // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- FilterPicker is a building block; consumers own the filter selection BI, so a generic trigger event would be redundant noise
                        noBi: true,
                        ref: anchorElRef,
                        testId: "".concat(testId, "-trigger"),
                        suffixIcon: anchorEl ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {}),
                        onClick: (e)=>setAnchorEl(e.currentTarget),
                        highlight: isHighlighted,
                        children: label
                    }),
                    type === 'button' && /*#__PURE__*/ _jsx(Button, {
                        // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- FilterPicker is a building block; consumers own the filter selection BI, so a generic trigger event would be redundant noise
                        noBi: true,
                        skin: "primaryOutline",
                        ref: anchorElRef,
                        testId: "".concat(testId, "-trigger"),
                        onClick: (e)=>setAnchorEl(e.currentTarget),
                        prefixIcon: props.type === 'button' ? props.prefixIcon : undefined,
                        children: props.type === 'button' || props.type === 'textButton' ? props.buttonTitle || title : title
                    }),
                    type === 'textButton' && /*#__PURE__*/ _jsx(TextButton, {
                        // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- FilterPicker is a building block; consumers own the filter selection BI, so a generic trigger event would be redundant noise
                        noBi: true,
                        type: "button",
                        testId: "".concat(testId, "-trigger"),
                        onClick: (e)=>setAnchorEl(e.currentTarget),
                        underline: "none",
                        skin: hasError ? 'error' : 'primary',
                        "aria-invalid": hasError,
                        children: props.type === 'button' || props.type === 'textButton' ? props.buttonTitle || title : title
                    }),
                    removable && /*#__PURE__*/ _jsx("button", {
                        className: styles.remove,
                        "data-testid": "".concat(testId, "-remove"),
                        onClick: onRemove,
                        children: /*#__PURE__*/ _jsx(CloseCross, {
                            size: 6
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Popover, {
                open: !!anchorEl,
                anchorEl: anchorEl,
                onClose: ()=>closePopover({
                        revert: true
                    }),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: styles.popover,
                    style: {
                        width: popoverWidth
                    },
                    "data-testid": "".concat(testId, "-popover"),
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.closeButtonContainer,
                            children: /*#__PURE__*/ _jsx(CloseButton, {
                                // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- FilterPicker is a building block; CloseButton self-logs its own close event and consumers own the filter BI
                                noBi: true,
                                testId: "".concat(testId, "-close"),
                                size: "small",
                                onClick: ()=>{
                                    closePopover({
                                        revert: true
                                    });
                                }
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph1",
                                    skin: "secondary",
                                    testId: "".concat(testId, "-title"),
                                    children: title
                                }),
                                headerContent
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.filters,
                            children: children
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.footer,
                            children: /*#__PURE__*/ _jsx(Button, {
                                noBi: true,
                                testId: "".concat(testId, "-apply"),
                                fluid: true,
                                disabled: disableApply,
                                onClick: handleApply,
                                children: applyLabel
                            })
                        })
                    ]
                })
            })
        ]
    });
};
